<script lang="ts">
let activeTooltipCloser: (() => void) | null = null
</script>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'

type TooltipAs = 'span' | 'div' | 'button'
type TooltipPlacement = 'top' | 'bottom'
type TooltipAlign = 'start' | 'center' | 'end'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
  as?: TooltipAs
  placement?: TooltipPlacement
  align?: TooltipAlign
  disabled?: boolean
  offset?: number
  maxWidth?: number
  avoidSelector?: string
  dismissOnClick?: boolean
}>(), {
  as: 'span',
  placement: 'top',
  align: 'center',
  disabled: false,
  offset: 10,
  maxWidth: 260,
  avoidSelector: '',
  dismissOnClick: false,
})

const anchor = ref<HTMLElement | null>(null)
const panel = ref<HTMLElement | null>(null)
const open = ref(false)
const position = reactive({
  left: 0,
  top: 0,
  arrowLeft: 0,
  placement: props.placement as TooltipPlacement,
})

const panelStyle = computed(() => ({
  left: `${position.left}px`,
  top: `${position.top}px`,
  maxWidth: `min(${props.maxWidth}px, calc(100vw - 16px))`,
}))

const arrowStyle = computed(() => ({
  left: `${position.arrowLeft}px`,
}))

function show(): void {
  if (props.disabled || open.value) return
  if (activeTooltipCloser && activeTooltipCloser !== hide) {
    activeTooltipCloser()
  }
  open.value = true
  activeTooltipCloser = hide
  addListeners()
  void nextTick(updatePosition)
}

function hide(): void {
  if (!open.value) return
  open.value = false
  if (activeTooltipCloser === hide) activeTooltipCloser = null
  removeListeners()
}

function hideAndReleaseFocus(): void {
  hide()
  const focused = document.activeElement
  if (focused instanceof HTMLElement && anchor.value?.contains(focused)) {
    focused.blur()
  }
}

function hideOnPointerLeave(): void {
  const focused = document.activeElement
  if (focused && anchor.value?.contains(focused)) return
  hide()
}

function handleDocumentPointerDown(event: PointerEvent): void {
  const target = event.target
  if (
    target instanceof Node &&
    (anchor.value?.contains(target) || panel.value?.contains(target))
  ) {
    return
  }
  hideAndReleaseFocus()
}

function updatePosition(): void {
  const anchorEl = anchor.value
  const panelEl = panel.value
  if (!anchorEl || !panelEl || !open.value) return

  const margin = 8
  const anchorRect = anchorEl.getBoundingClientRect()
  const panelRect = panelEl.getBoundingClientRect()
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let left = preferredLeft(anchorRect, panelRect.width)
  left = clamp(left, margin, Math.max(margin, viewportWidth - panelRect.width - margin))

  let placement = props.placement
  let top = placement === 'top'
    ? anchorRect.top - panelRect.height - props.offset
    : anchorRect.bottom + props.offset

  const bottomTop = anchorRect.bottom + props.offset
  const topTop = anchorRect.top - panelRect.height - props.offset
  if (placement === 'top' && top < margin && bottomTop + panelRect.height <= viewportHeight - margin) {
    placement = 'bottom'
    top = bottomTop
  } else if (placement === 'bottom' && top + panelRect.height > viewportHeight - margin && topTop >= margin) {
    placement = 'top'
    top = topTop
  }

  const avoidRect = avoidedElementRect(anchorEl)
  if (
    avoidRect &&
    rectanglesOverlap(
      left,
      top,
      panelRect.width,
      panelRect.height,
      avoidRect,
    )
  ) {
    const aboveTop = avoidRect.top - panelRect.height - props.offset
    const belowTop = avoidRect.bottom + props.offset
    if (aboveTop >= margin) {
      placement = 'top'
      top = aboveTop
    } else if (belowTop + panelRect.height <= viewportHeight - margin) {
      placement = 'bottom'
      top = belowTop
    }
  }
  top = clamp(top, margin, Math.max(margin, viewportHeight - panelRect.height - margin))

  position.left = left
  position.top = top
  position.placement = placement
  position.arrowLeft = clamp(anchorRect.left + anchorRect.width / 2 - left, 14, Math.max(14, panelRect.width - 14))
}

function preferredLeft(anchorRect: DOMRect, panelWidth: number): number {
  switch (props.align) {
    case 'start':
      return anchorRect.left
    case 'end':
      return anchorRect.right - panelWidth
    default:
      return anchorRect.left + anchorRect.width / 2 - panelWidth / 2
  }
}

function avoidedElementRect(anchorEl: HTMLElement): DOMRect | null {
  if (!props.avoidSelector) return null
  const scope = anchorEl.closest('.player-dock') ?? document
  const rects = [...scope.querySelectorAll<HTMLElement>(props.avoidSelector)]
    .map(element => element.getBoundingClientRect())
    .filter(rect => rect.width > 0 && rect.height > 0)
  if (rects.length === 0) return null
  const left = Math.min(...rects.map(rect => rect.left))
  const top = Math.min(...rects.map(rect => rect.top))
  const right = Math.max(...rects.map(rect => rect.right))
  const bottom = Math.max(...rects.map(rect => rect.bottom))
  return {
    x: left,
    y: top,
    left,
    top,
    width: right - left,
    height: bottom - top,
    right,
    bottom,
    toJSON: () => ({}),
  } as DOMRect
}

function rectanglesOverlap(
  left: number,
  top: number,
  width: number,
  height: number,
  other: DOMRect,
): boolean {
  return (
    left < other.right &&
    left + width > other.left &&
    top < other.bottom &&
    top + height > other.top
  )
}

function handleClick(): void {
  if (props.dismissOnClick) hide()
}

function addListeners(): void {
  window.addEventListener('resize', updatePosition, { passive: true })
  window.addEventListener('scroll', updatePosition, { passive: true, capture: true })
  document.addEventListener('pointerdown', handleDocumentPointerDown, { capture: true })
}

function removeListeners(): void {
  window.removeEventListener('resize', updatePosition)
  window.removeEventListener('scroll', updatePosition, { capture: true })
  document.removeEventListener('pointerdown', handleDocumentPointerDown, { capture: true })
}

function cleanup(): void {
  removeListeners()
  if (activeTooltipCloser === hide) activeTooltipCloser = null
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

watch(() => props.disabled, disabled => {
  if (disabled) hide()
})

onBeforeUnmount(cleanup)
</script>

<template>
  <component
    :is="as"
    ref="anchor"
    class="floating-tooltip-anchor"
    v-bind="$attrs"
    @pointerenter="show"
    @pointerleave="hideOnPointerLeave"
    @focusin="show"
    @focusout="hide"
    @keydown.escape="hide"
    @click="handleClick"
  >
    <slot />
  </component>
  <Teleport to="body">
    <div
      v-if="open"
      ref="panel"
      class="floating-tooltip-panel"
      :class="`placement-${position.placement}`"
      :style="panelStyle"
      role="tooltip"
    >
      <slot name="content" />
      <span class="floating-tooltip-arrow" :style="arrowStyle"></span>
    </div>
  </Teleport>
</template>

<style scoped>
.floating-tooltip-anchor {
  min-width: 0;
}

.floating-tooltip-panel {
  position: fixed;
  z-index: 100100;
  padding: 9px 10px;
  border: 1px solid rgb(107 126 119 / 0.46);
  background: rgb(6 10 11 / 0.97);
  box-shadow: 0 10px 24px rgb(0 0 0 / 0.48);
  color: #d6ddd6;
  overflow-wrap: anywhere;
  pointer-events: none;
}

.floating-tooltip-arrow {
  position: absolute;
  width: 10px;
  height: 10px;
  border-right: 1px solid rgb(107 126 119 / 0.46);
  border-bottom: 1px solid rgb(107 126 119 / 0.46);
  background: rgb(6 10 11 / 0.97);
}

.floating-tooltip-panel.placement-top .floating-tooltip-arrow {
  bottom: -6px;
  transform: translateX(-50%) rotate(45deg);
}

.floating-tooltip-panel.placement-bottom .floating-tooltip-arrow {
  top: -6px;
  transform: translateX(-50%) rotate(225deg);
}
</style>
